import React, { useEffect, useMemo, useRef, useState } from "react"
import { accessFromProps } from "../lib/book-access"
import { apiErrorMessage } from "../lib/api-errors"
import { canApproveDocument } from "../lib/document-approval"
import { BookShell } from "../components/book-shell"
import { PageHeader } from "../components/page-header"
import { DocumentLineRows } from "../components/document-line-rows"
import { QuoteLineRows, quoteLinesTotalCents } from "../components/quote-line-rows"
import { CurrencySearchSelect, FORM_INPUT_CLASS } from "../components/currency-search-select"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card"
import { cn } from "../lib/utils"
import { appConfirm } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"
import { GST_STATE_OPTIONS } from "../lib/gst-states"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      ...(options.headers || {}),
    },
    ...options,
  })

  const contentType = res.headers.get("content-type") || ""
  const data = contentType.includes("application/json") ? await res.json() : null

  if (!res.ok) {
    const message = data?.message || (data?.error ? `${data.error}` : `Request failed (${res.status})`)
    const details = data?.details ? JSON.stringify(data.details) : ""
    throw new Error(details ? `${message}: ${details}` : message)
  }

  return data
}

function centsToAmount(cents) {
  if (cents === null || cents === undefined) return ""
  return (Number(cents) / 100).toFixed(2)
}

function amountToCents(amount) {
  const n = Number.parseFloat(String(amount || "0").trim())
  if (Number.isNaN(n)) return 0
  return Math.max(0, Math.round(n * 100))
}

function TextInput({ label, value, onChange, type = "text", placeholder, error, readOnly = false, hint }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        readOnly={readOnly}
        disabled={readOnly}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          FORM_INPUT_CLASS,
          readOnly && "cursor-default bg-muted/30 text-muted-foreground",
        )}
      />
      {hint ? <span className="text-[11px] text-muted-foreground">{hint}</span> : null}
      {error ? <span className="text-[12px] text-red-600">{error}</span> : null}
    </label>
  )
}

function SelectInput({ label, value, onChange, options, error }) {
  return (
    <label className="grid gap-0.5 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={FORM_INPUT_CLASS}
      >
        <option value="">Select...</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {error ? <span className="text-xs text-red-600">{error}</span> : null}
    </label>
  )
}

function nextDocNumber(items, fieldName, prefix) {
  let max = 0
  items.forEach((item) => {
    const raw = String(item[fieldName] || "")
    const match = raw.match(/(\d+)\s*$/)
    if (!match) return
    const n = Number.parseInt(match[1], 10)
    if (!Number.isNaN(n)) max = Math.max(max, n)
  })
  return `${prefix}${String(max + 1).padStart(4, "0")}`
}

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

const QUOTE_TYPE_OPTIONS = [
  { value: "stores", label: "Stores" },
  { value: "spares", label: "Spares" },
  { value: "provision", label: "Provision" },
  { value: "services", label: "Services" },
  { value: "repairs", label: "Repairs" },
  { value: "annual_repairs", label: "Annual repairs" },
]

function buildListUrl(endpoint, listParams) {
  if (!endpoint) return null
  const sp = new URLSearchParams()
  if (listParams) {
    Object.entries(listParams).forEach(([k, v]) => {
      if (v != null && v !== "") sp.set(k, String(v))
    })
  }
  const q = sp.toString()
  return q ? `${endpoint}?${q}` : endpoint
}

function currentOrganizationIdFromMeta() {
  const raw =
    typeof document !== "undefined"
      ? document.querySelector("meta[name='current-organization-id']")?.getAttribute("content")
      : null
  if (raw == null || String(raw).trim() === "") return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

function setCurrentOrganizationMeta(id) {
  const el = document.querySelector("meta[name='current-organization-id']")
  if (el && id != null) el.setAttribute("content", String(id))
}

async function selectOrganizationSession(id) {
  await fetchJson(`/api/organizations/${id}/select`, { method: "POST", body: "{}" })
  setCurrentOrganizationMeta(id)
}

function InvoiceSaveConfirmDialog({ summary, saving, onConfirm, onCancel }) {
  if (!summary) return null
  const { org, customer, payload, lines, editing } = summary
  const lineTotal = lines.reduce((sum, row) => sum + (row.amount_cents || 0), 0)
  const totalCents = lineTotal > 0 ? lineTotal : payload.total_cents

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invoice-confirm-title"
    >
      <Card className="max-h-[90vh] w-full max-w-lg overflow-auto shadow-lg">
        <CardHeader>
          <CardTitle id="invoice-confirm-title">{editing ? "Confirm invoice update" : "Confirm new invoice"}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 text-sm">
          <p className="text-muted-foreground">
            Review the company and customer details below. The invoice is only saved after you confirm.
          </p>
          <div className="rounded-lg border border-border/90 bg-muted/20 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">From company</div>
            <div className="mt-1 font-medium text-foreground">{org?.name || "—"}</div>
            <div className="text-xs text-muted-foreground">
              Code: {org?.company_code || "—"} · Currency: {org?.currency || payload.currency || "—"}
              {org?.state_code ? ` · State: ${org.state_code}` : ""}
              {org?.gstin ? ` · GSTIN: ${org.gstin}` : ""}
            </div>
          </div>
          <div className="rounded-lg border border-border/90 bg-muted/20 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Bill to (customer)</div>
            <div className="mt-1 font-medium text-foreground">{customer?.name || "—"}</div>
            <div className="text-xs text-muted-foreground">Code: {customer?.party_code || "—"}</div>
            {customer?.email ? <div className="text-xs text-muted-foreground">Email: {customer.email}</div> : null}
            {customer?.phone ? <div className="text-xs text-muted-foreground">Phone: {customer.phone}</div> : null}
            {customer?.address ? <div className="text-xs text-muted-foreground">Address: {customer.address}</div> : null}
            {customer?.tax_number ? <div className="text-xs text-muted-foreground">Tax #: {customer.tax_number}</div> : null}
            {customer?.state_code ? <div className="text-xs text-muted-foreground">State: {customer.state_code}</div> : null}
          </div>
          <div className="rounded-lg border border-border/90 p-3">
            <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Invoice</div>
            <dl className="mt-2 grid gap-1 text-xs">
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Invoice #</dt>
                <dd className="font-medium">{payload.invoice_number || "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Date</dt>
                <dd>{payload.invoice_date || "—"}</dd>
              </div>
              {payload.due_date ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Due date</dt>
                  <dd>{payload.due_date}</dd>
                </div>
              ) : null}
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Status</dt>
                <dd className="capitalize">{payload.status || "—"}</dd>
              </div>
              <div className="flex justify-between gap-2">
                <dt className="text-muted-foreground">Currency</dt>
                <dd>{payload.currency || "—"}</dd>
              </div>
              {payload.notes ? (
                <div className="flex justify-between gap-2">
                  <dt className="text-muted-foreground">Notes</dt>
                  <dd className="text-right">{payload.notes}</dd>
                </div>
              ) : null}
            </dl>
          </div>
          {lines.length > 0 ? (
            <div className="overflow-x-auto rounded-lg border border-border/90">
              <table className="min-w-full text-xs">
                <thead>
                  <tr className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                    <th className="px-2 py-1.5 font-medium">Description</th>
                    <th className="px-2 py-1.5 font-medium text-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0">
                      <td className="px-2 py-1">{row.description || "—"}</td>
                      <td className="px-2 py-1 text-right">{centsToAmount(row.amount_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          <div className="flex justify-between border-t border-border pt-2 text-sm font-medium">
            <span>Total</span>
            <span>
              {payload.currency || org?.currency || ""} {centsToAmount(totalCents || payload.total_cents)}
            </span>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>
              Back to edit
            </Button>
            <Button type="button" onClick={onConfirm} disabled={saving}>
              {saving ? "Saving…" : editing ? "Confirm & save" : "Confirm & create invoice"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export function SalesResourceCrud({
  sectionKey,
  sectionTitle,
  listParams = null,
  hideForm = false,
  access = {},
  enableVendorQuoteReselling = false,
}) {
  const canEnter = Boolean(access.can_enter_transactions)
  const canApprove = Boolean(access.can_approve_documents)
  const needsCompany = Boolean(access.needs_company)
  const setupCompany = needsCompany && Boolean(access.can_create_company)

  const [items, setItems] = useState([])
  const [customers, setCustomers] = useState([])
  const [gstRates, setGstRates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [fieldErrors, setFieldErrors] = useState({})
  const [query, setQuery] = useState("")
  const [editingId, setEditingId] = useState(null)
  const editingIdRef = useRef(editingId)
  const [page, setPage] = useState(1)
  const PAGE_SIZE = 10
  const isInvoices = sectionKey === "invoices"
  const isStructuredQuotes = sectionKey === "quotes"
  const [organizations, setOrganizations] = useState([])
  const [selectedOrgId, setSelectedOrgId] = useState(() => currentOrganizationIdFromMeta())
  const [orgSwitching, setOrgSwitching] = useState(false)
  usePageLoading(loading || orgSwitching, orgSwitching ? "Switching company…" : "Loading…")
  const [pendingInvoiceSave, setPendingInvoiceSave] = useState(null)
  const [invoiceSaving, setInvoiceSaving] = useState(false)
  const [invoiceNumberLoading, setInvoiceNumberLoading] = useState(false)
  const [quoteNumberLoading, setQuoteNumberLoading] = useState(false)

  useEffect(() => {
    editingIdRef.current = editingId
  }, [editingId])

  useEffect(() => {
    if (!isInvoices) return
    let alive = true
    ;(async () => {
      try {
        const data = await fetchJson("/api/organizations")
        if (!alive) return
        const list = Array.isArray(data) ? data : []
        setOrganizations(list)
        const metaId = currentOrganizationIdFromMeta()
        if (metaId && list.some((o) => Number(o.id) === metaId)) {
          setSelectedOrgId(metaId)
        } else if (list.length > 0) {
          setSelectedOrgId(Number(list[0].id))
        }
      } catch {
        if (alive) setOrganizations([])
      }
    })()
    return () => {
      alive = false
    }
  }, [isInvoices])

  const config = useMemo(() => {
    switch (sectionKey) {
      case "customers":
        return {
          endpoint: "/api/sales/customers",
          wrap: "customer",
          fields: [
            { name: "name", label: "Name", kind: "text", required: true },
            { name: "email", label: "Email", kind: "text" },
            { name: "phone", label: "Phone", kind: "text" },
            { name: "address", label: "Address", kind: "text" },
            { name: "tax_number", label: "Tax Number", kind: "text" },
            { name: "state_code", label: "State (GST)", kind: "select", options: GST_STATE_OPTIONS },
          ],
          columns: ["name", "email", "phone", "state_code"],
        }
      case "quotes":
        return {
          endpoint: "/api/sales/quotes",
          wrap: "quote",
          smartDefaults: () => ({
            quote_number: "",
            quote_date: todayISO(),
            quote_type: enableVendorQuoteReselling ? "services" : "",
            status: "draft",
            currency: "USD",
            lines: [{ description: "", quantity: "1", unit_price: "", discount: "" }],
          }),
          fields: [
            { name: "customer_id", label: "Customer", kind: "customer", required: true },
            {
              name: "quote_number",
              label: "Quote #",
              kind: "text",
              required: true,
              readOnly: true,
              hint: "Auto-generated: FROM-TO-YEAR-Q#### (company code, customer code, year, sequence).",
            },
            { name: "quote_date", label: "Quote Date", kind: "date", required: true },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "sent", label: "Sent" },
                { value: "accepted", label: "Accepted" },
                { value: "rejected", label: "Rejected" },
              ],
            },
            { name: "total_cents", label: "Total", kind: "amount", required: true, readOnly: true },
            { name: "currency", label: "Currency", kind: "currency" },
            ...(enableVendorQuoteReselling
              ? [
                  {
                    name: "quote_type",
                    label: "Quote type",
                    kind: "select",
                    required: true,
                    options: QUOTE_TYPE_OPTIONS,
                  },
                ]
              : []),
            { name: "notes", label: "Notes", kind: "text", wide: true },
          ],
          columns: enableVendorQuoteReselling
            ? ["quote_number", "quote_date", "customer", "quote_type", "status", "total_cents"]
            : ["quote_number", "quote_date", "customer", "status", "total_cents"],
        }
      case "sales-orders":
        return {
          endpoint: "/api/sales/sales_orders",
          wrap: "sales_order",
          smartDefaults: (allItems) => ({
            order_number: nextDocNumber(allItems, "order_number", "SO-"),
            order_date: todayISO(),
            status: "draft",
          }),
          fields: [
            { name: "customer_id", label: "Customer", kind: "customer", required: true },
            { name: "order_number", label: "Order #", kind: "text", required: true },
            { name: "order_date", label: "Order Date", kind: "date", required: true },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "sent", label: "Sent" },
                { value: "closed", label: "Closed" },
              ],
            },
            { name: "total_cents", label: "Total", kind: "amount", required: true },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" },
          ],
          columns: ["order_number", "order_date", "customer", "status", "total_cents"],
        }
      case "invoices":
        return {
          endpoint: "/api/sales/invoices",
          wrap: "invoice",
          smartDefaults: () => ({
            invoice_number: "",
            invoice_date: todayISO(),
            status: "draft",
            lines: [{ description: "", amount: "", hsn_sac: "", gst_tax_rate_id: "" }],
          }),
          fields: [
            { name: "customer_id", label: "Customer", kind: "customer", required: true },
            {
              name: "invoice_number",
              label: "Invoice #",
              kind: "text",
              required: true,
              readOnly: true,
              hint: "Auto-generated: FROM-TO-YEAR-#### (company codes, calendar year, sequence).",
            },
            { name: "invoice_date", label: "Invoice Date", kind: "date", required: true },
            { name: "due_date", label: "Due Date", kind: "date" },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "unpaid", label: "Unpaid" },
                { value: "paid", label: "Paid" },
                { value: "overdue", label: "Overdue" },
                { value: "void", label: "Void" },
                { value: "cancelled", label: "Cancelled" },
              ],
            },
            { name: "total_cents", label: "Total", kind: "amount", required: true },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" },
          ],
          columns: ["invoice_number", "invoice_date", "customer", "status", "approval_status", "total_cents"],
        }
      case "recurring-invoices":
        return {
          endpoint: "/api/sales/recurring_invoices",
          wrap: "recurring_invoice",
          smartDefaults: () => ({
            start_date: todayISO(),
            interval_count: 1,
            interval_unit: "month",
            active: true,
          }),
          fields: [
            { name: "customer_id", label: "Customer", kind: "customer", required: true },
            { name: "invoice_number_prefix", label: "Prefix", kind: "text" },
            { name: "start_date", label: "Start Date", kind: "date", required: true },
            { name: "next_run_on", label: "Next Run On", kind: "date" },
            { name: "description", label: "Description", kind: "text", required: true },
            { name: "total_cents", label: "Total", kind: "amount", required: true },
            { name: "interval_count", label: "Interval Count", kind: "number" },
            {
              name: "interval_unit",
              label: "Interval Unit",
              kind: "select",
              options: [
                { value: "day", label: "Day" },
                { value: "week", label: "Week" },
                { value: "month", label: "Month" },
                { value: "year", label: "Year" },
              ],
            },
            { name: "active", label: "Active", kind: "boolean" },
            { name: "notes", label: "Notes", kind: "text" },
          ],
          columns: ["start_date", "customer", "description", "total_cents", "active"],
        }
      case "delivery-challans":
        return {
          endpoint: "/api/sales/delivery_challans",
          wrap: "delivery_challan",
          smartDefaults: (allItems) => ({
            challan_number: nextDocNumber(allItems, "challan_number", "DC-"),
            challan_date: todayISO(),
            status: "draft",
          }),
          fields: [
            { name: "customer_id", label: "Customer", kind: "customer", required: true },
            { name: "challan_number", label: "Challan #", kind: "text", required: true },
            { name: "challan_date", label: "Challan Date", kind: "date", required: true },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "draft", label: "Draft" },
                { value: "sent", label: "Sent" },
                { value: "delivered", label: "Delivered" },
              ],
            },
            { name: "notes", label: "Notes", kind: "text" },
          ],
          columns: ["challan_number", "challan_date", "customer", "status"],
        }
      case "payments-received":
        return {
          endpoint: "/api/sales/payments_received",
          wrap: "payment_received",
          smartDefaults: (allItems) => ({
            payment_number: nextDocNumber(allItems, "payment_number", "PR-"),
            payment_date: todayISO(),
          }),
          fields: [
            { name: "customer_id", label: "Customer", kind: "customer", required: true },
            { name: "payment_number", label: "Payment #", kind: "text", required: true },
            { name: "payment_date", label: "Payment Date", kind: "date", required: true },
            { name: "amount_cents", label: "Amount", kind: "amount", required: true },
            { name: "method", label: "Method", kind: "text" },
            { name: "reference", label: "Reference", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" },
          ],
          columns: ["payment_number", "payment_date", "customer", "amount_cents", "method"],
        }
      case "credit-notes":
        return {
          endpoint: "/api/sales/credit_notes",
          wrap: "credit_note",
          smartDefaults: (allItems) => ({
            credit_number: nextDocNumber(allItems, "credit_number", "SCN-"),
            credit_date: todayISO(),
            status: "open",
            lines: [{ description: "", amount: "", hsn_sac: "", gst_tax_rate_id: "" }],
          }),
          fields: [
            { name: "customer_id", label: "Customer", kind: "customer", required: true },
            { name: "credit_number", label: "Credit #", kind: "text", required: true },
            { name: "credit_date", label: "Credit Date", kind: "date", required: true },
            {
              name: "status",
              label: "Status",
              kind: "select",
              options: [
                { value: "open", label: "Open" },
                { value: "applied", label: "Applied" },
                { value: "void", label: "Void" },
              ],
            },
            { name: "currency", label: "Currency", kind: "text" },
            { name: "notes", label: "Notes", kind: "text" },
          ],
          columns: ["credit_number", "credit_date", "customer", "status", "amount_cents"],
        }
      default:
        return null
    }
  }, [sectionKey, enableVendorQuoteReselling])

  const gstRateSelectOptions = useMemo(
    () => gstRates.map((r) => ({ value: String(r.id), label: `${r.code} (${r.rate_percent}%)` })),
    [gstRates],
  )

  const listUrl = useMemo(() => buildListUrl(config?.endpoint, listParams), [config, listParams])

  const [form, setForm] = useState({})

  useEffect(() => {
    let alive = true
    async function load() {
      if (!config) {
        setLoading(false)
        return
      }
      try {
        setLoading(true)
        setError("")
        const [itemsData, customerData] = await Promise.all([
          fetchJson(listUrl),
          sectionKey === "customers" ? Promise.resolve([]) : fetchJson("/api/sales/customers"),
        ])

        if (!alive) return
        setItems(itemsData || [])
        setCustomers(customerData || [])
        if (!editingIdRef.current) {
          setForm(config.smartDefaults ? config.smartDefaults(itemsData || []) : {})
        }
        setPage(1)
      } catch (e) {
        if (!alive) return
        setError(e.message || "Failed to load data")
      } finally {
        if (!alive) return
        setLoading(false)
      }
    }

    load()
    return () => {
      alive = false
    }
  }, [config, sectionKey, listUrl])

  useEffect(() => {
    setPage(1)
  }, [query, sectionKey])

  useEffect(() => {
    if (sectionKey !== "invoices" && sectionKey !== "credit-notes") {
      setGstRates([])
      return
    }
    let alive = true
    ;(async () => {
      try {
        const data = await fetchJson("/api/accounting/gst_tax_rates")
        if (alive) setGstRates(Array.isArray(data) ? data : [])
      } catch {
        if (alive) setGstRates([])
      }
    })()
    return () => {
      alive = false
    }
  }, [sectionKey])

  function customerNameFor(id) {
    const c = customers.find((x) => String(x.id) === String(id))
    return c ? c.name : ""
  }

  function resetForm() {
    setEditingId(null)
    setFieldErrors({})
    setPendingInvoiceSave(null)
    setForm(config?.smartDefaults ? config.smartDefaults(items) : {})
  }

  function setField(name, value) {
    setFieldErrors((prev) => ({ ...prev, [name]: null }))
    setForm((f) => ({ ...f, [name]: value }))
  }

  async function refreshQuoteNumber() {
    if (!isStructuredQuotes || editingId) return
    if (!form.customer_id) {
      setField("quote_number", "")
      return
    }
    try {
      setQuoteNumberLoading(true)
      const sp = new URLSearchParams({
        customer_id: String(form.customer_id),
        quote_date: form.quote_date || todayISO(),
      })
      if (form.quote_type) sp.set("quote_type", form.quote_type)
      const data = await fetchJson(`/api/sales/quotes/next_number?${sp.toString()}`)
      setField("quote_number", data.quote_number || "")
    } catch {
      setField("quote_number", "")
    } finally {
      setQuoteNumberLoading(false)
    }
  }

  async function refreshInvoiceNumber() {
    if (!isInvoices || editingId) return
    if (!form.customer_id || !selectedOrgId) {
      setField("invoice_number", "")
      return
    }
    try {
      setInvoiceNumberLoading(true)
      const sp = new URLSearchParams({
        customer_id: String(form.customer_id),
        invoice_date: form.invoice_date || todayISO(),
      })
      const data = await fetchJson(`/api/sales/invoices/next_number?${sp.toString()}`)
      setField("invoice_number", data.invoice_number || "")
    } catch {
      setField("invoice_number", "")
    } finally {
      setInvoiceNumberLoading(false)
    }
  }

  useEffect(() => {
    if (!isInvoices || editingId) return
    refreshInvoiceNumber()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInvoices, editingId, form.customer_id, form.invoice_date, selectedOrgId])

  useEffect(() => {
    if (!isStructuredQuotes || editingId) return
    refreshQuoteNumber()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStructuredQuotes, editingId, form.customer_id, form.quote_date, form.quote_type])

  async function switchInvoiceOrganization(orgId) {
    const id = Number(orgId)
    if (!id || Number.isNaN(id) || id === selectedOrgId) return
    try {
      setOrgSwitching(true)
      setError("")
      setPendingInvoiceSave(null)
      await selectOrganizationSession(id)
      setSelectedOrgId(id)
      setEditingId(null)
      const [itemsData, customerData, gstData] = await Promise.all([
        fetchJson(listUrl),
        fetchJson("/api/sales/customers"),
        fetchJson("/api/accounting/gst_tax_rates"),
      ])
      setItems(itemsData || [])
      setCustomers(customerData || [])
      setGstRates(Array.isArray(gstData) ? gstData : [])
      setForm(config.smartDefaults ? config.smartDefaults(itemsData || []) : {})
      setFieldErrors({})
      setPage(1)
    } catch (e) {
      setError(e.message || "Could not switch company")
    } finally {
      setOrgSwitching(false)
    }
  }

  function buildPayloadFromForm() {
    const nextFieldErrors = {}
    const payload = {}
    config.fields.forEach((f) => {
      const raw = form[f.name]
      if (f.kind === "amount") payload[f.name] = amountToCents(raw)
      else if (f.kind === "number") {
        if (raw === "" || raw === null || raw === undefined) {
          payload[f.name] = f.name === "interval_count" ? 1 : null
        } else {
          const n = Number.parseInt(String(raw), 10)
          payload[f.name] = Number.isNaN(n) ? (f.name === "interval_count" ? 1 : null) : n
        }
      } else if (f.kind === "boolean") payload[f.name] = Boolean(raw)
      else if (f.kind === "customer") payload[f.name] = raw === "" ? null : Number.parseInt(raw, 10)
      else if (f.name === "currency") payload[f.name] = raw === "" || raw == null ? "INR" : raw
      else payload[f.name] = raw === "" ? null : raw
    })

    config.fields.forEach((f) => {
      if (!f.required) return
      const value = payload[f.name]
      if (f.kind === "boolean") return
      if (value === null || value === undefined || value === "") {
        nextFieldErrors[f.name] = `${f.label} is required`
      }
    })
    config.fields.forEach((f) => {
      if (f.kind !== "amount" || !f.required) return
      if (payload[f.name] === 0) {
        nextFieldErrors[f.name] = `${f.label} must be greater than zero`
      }
    })

    if (isStructuredQuotes) {
      if (enableVendorQuoteReselling && !form.quote_type) {
        nextFieldErrors.quote_type = "Quote type is required"
      }
      if (!editingId && (!form.quote_number || String(form.quote_number).trim() === "")) {
        nextFieldErrors.quote_number = "Select a customer to generate a quote number"
      }
      payload.lines = (form.lines || [])
        .map((row) => {
          const qty = row.quantity === "" || row.quantity == null ? 1 : Number.parseFloat(String(row.quantity))
          const unitPriceCents = amountToCents(row.unit_price)
          const discountCents = amountToCents(row.discount)
          const subtotal = Math.round((Number.isNaN(qty) ? 1 : qty) * unitPriceCents)
          return {
            description: row.description || "",
            quantity: Number.isNaN(qty) ? 1 : qty,
            unit_price_cents: unitPriceCents,
            discount_cents: discountCents,
            line_total_cents: Math.max(0, subtotal - discountCents),
          }
        })
        .filter((row) => row.line_total_cents > 0 || String(row.description || "").trim().length > 0)
        .map(({ line_total_cents: _drop, ...row }) => row)
      const lineTotal = payload.lines.reduce((sum, row) => sum + row.line_total_cents, 0)
      if (lineTotal > 0) {
        payload.total_cents = lineTotal
      } else if (!payload.total_cents || payload.total_cents <= 0) {
        nextFieldErrors.total_cents = "Add at least one line item or enter a total"
      }
    }

    if (isInvoices) {
      if (!selectedOrgId) {
        nextFieldErrors.company = "Select a company"
      }
      if (!form.invoice_number || String(form.invoice_number).trim() === "") {
        nextFieldErrors.invoice_number = "Select a customer to generate an invoice number"
      }
      payload.lines = (form.lines || [])
        .map((row) => {
          const gid = row.gst_tax_rate_id ? Number.parseInt(String(row.gst_tax_rate_id), 10) : null
          return {
            description: row.description || "",
            amount_cents: amountToCents(row.amount),
            hsn_sac: row.hsn_sac || null,
            gst_tax_rate_id: gid && !Number.isNaN(gid) ? gid : null,
          }
        })
        .filter((row) => row.amount_cents > 0 || String(row.description || "").trim().length > 0)
    }

    if (sectionKey === "credit-notes") {
      payload.lines = (form.lines || [])
        .map((row) => {
          const gid = row.gst_tax_rate_id ? Number.parseInt(String(row.gst_tax_rate_id), 10) : null
          return {
            description: row.description || "",
            amount_cents: amountToCents(row.amount),
            hsn_sac: row.hsn_sac || null,
            gst_tax_rate_id: gid && !Number.isNaN(gid) ? gid : null,
          }
        })
        .filter((row) => row.amount_cents > 0 || String(row.description || "").trim().length > 0)
      const lineTotal = payload.lines.reduce((sum, row) => sum + row.amount_cents, 0)
      if (lineTotal <= 0) {
        throw new Error("Add at least one line with an amount greater than zero.")
      }
      payload.amount_cents = lineTotal
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors)
      throw new Error("Please correct highlighted fields.")
    }
    setFieldErrors({})
    return payload
  }

  async function executeInvoiceSave() {
    if (!pendingInvoiceSave) return
    try {
      setInvoiceSaving(true)
      setError("")
      if (selectedOrgId) {
        await selectOrganizationSession(selectedOrgId)
      }
      const { payload } = pendingInvoiceSave
      const body = JSON.stringify({ invoice: payload })
      if (editingId) {
        await fetchJson(`${config.endpoint}/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson(config.endpoint, { method: "POST", body })
      }
      setPendingInvoiceSave(null)
      await reload()
      resetForm()
    } catch (e2) {
      setError(e2.message || "Save failed")
    } finally {
      setInvoiceSaving(false)
    }
  }

  function hydrateForEdit(item) {
    const next = {}
    config.fields.forEach((f) => {
      if (f.kind === "amount") {
        next[f.name] = centsToAmount(item[f.name])
      } else if (f.kind === "customer") {
        next[f.name] = item.customer_id ? String(item.customer_id) : ""
      } else if (f.kind === "boolean") {
        next[f.name] = Boolean(item[f.name])
      } else if (f.kind === "number") {
        const v = item[f.name]
        next[f.name] = v === null || v === undefined ? "" : String(v)
      } else {
        next[f.name] = item[f.name] ?? ""
      }
    })
    if (sectionKey === "quotes") {
      const raw = item.quote_lines || []
      next.lines = raw.length
        ? raw.map((l) => ({
            id: l.id,
            description: l.description || "",
            quantity: l.quantity == null ? "1" : String(l.quantity),
            unit_price: centsToAmount(l.unit_price_cents),
            discount: centsToAmount(l.discount_cents),
          }))
        : [{ description: "", quantity: "1", unit_price: "", discount: "" }]
    }
    if (sectionKey === "invoices") {
      const raw = item.invoice_lines || []
      next.lines = raw.length
        ? raw.map((l) => ({
            id: l.id,
            description: l.description || "",
            amount: centsToAmount(l.amount_cents),
            hsn_sac: l.hsn_sac || "",
            gst_tax_rate_id: l.gst_tax_rate_id ? String(l.gst_tax_rate_id) : "",
          }))
        : [{ description: "", amount: "", hsn_sac: "", gst_tax_rate_id: "" }]
    }
    if (sectionKey === "credit-notes") {
      const raw = item.credit_note_lines || []
      next.lines = raw.length
        ? raw.map((l) => ({
            id: l.id,
            description: l.description || "",
            amount: centsToAmount(l.amount_cents),
            hsn_sac: l.hsn_sac || "",
            gst_tax_rate_id: l.gst_tax_rate_id ? String(l.gst_tax_rate_id) : "",
          }))
        : [
            {
              description: "Credit",
              amount: centsToAmount(item.amount_cents),
              hsn_sac: "",
              gst_tax_rate_id: "",
            },
          ]
    }
    setEditingId(item.id)
    setFieldErrors({})
    setForm(next)
  }

  async function reload() {
    const data = await fetchJson(listUrl)
    setItems(data || [])
  }

  async function approveInvoice(id) {
    try {
      setError("")
      await fetchJson(`/api/sales/invoices/${id}/approve`, { method: "POST", body: "{}" })
      await reload()
    } catch (e2) {
      setError(apiErrorMessage(e2, "Approve failed"))
    }
  }

  async function rejectInvoice(id) {
    try {
      setError("")
      await fetchJson(`/api/sales/invoices/${id}/reject`, { method: "POST", body: "{}" })
      await reload()
    } catch (e2) {
      setError(e2.message || "Reject failed")
    }
  }

  async function onSubmit(e) {
    e.preventDefault()
    try {
      setError("")
      const payload = buildPayloadFromForm()

      if (isInvoices) {
        const sp = new URLSearchParams({
          customer_id: String(form.customer_id),
          invoice_date: form.invoice_date || todayISO(),
        })
        const preview = await fetchJson(`/api/sales/invoices/next_number?${sp.toString()}`)
        payload.invoice_number = preview.invoice_number
        setField("invoice_number", preview.invoice_number)

        const org =
          organizations.find((o) => Number(o.id) === Number(selectedOrgId)) ||
          organizations.find((o) => Number(o.id) === currentOrganizationIdFromMeta())
        const customer = customers.find((c) => String(c.id) === String(form.customer_id))
        if (org && preview.from_company_code) org.company_code = preview.from_company_code
        if (customer && preview.to_party_code) customer.party_code = preview.to_party_code
        setPendingInvoiceSave({
          payload,
          lines: payload.lines || [],
          org: org || null,
          customer: customer || null,
          editing: Boolean(editingId),
        })
        return
      }

      if (isStructuredQuotes && !editingId) {
        delete payload.quote_number
      }

      const body = JSON.stringify({ [config.wrap]: payload })

      if (editingId) {
        await fetchJson(`${config.endpoint}/${editingId}`, { method: "PUT", body })
      } else {
        await fetchJson(config.endpoint, { method: "POST", body })
      }

      await reload()
      resetForm()
    } catch (e2) {
      setError(e2.message || "Save failed")
    }
  }

  function downloadQuotePdf(id) {
    window.open(`/api/sales/quotes/${id}/pdf`, "_blank", "noopener,noreferrer")
  }

  async function onDelete(id) {
    if (!(await appConfirm("Delete this record?", { title: "Delete", danger: true, confirmLabel: "Delete" }))) return
    try {
      setError("")
      await fetchJson(`${config.endpoint}/${id}`, { method: "DELETE" })
      await reload()
      if (editingId === id) resetForm()
    } catch (e) {
      setError(e.message || "Delete failed")
    }
  }

  const customerOptions = customers.map((c) => ({ value: String(c.id), label: c.name }))
  const organizationOptions = organizations.map((o) => ({
    value: String(o.id),
    label: o.company_code ? `${o.name} (${o.company_code})` : o.name,
  }))
  const selectedOrganization =
    organizations.find((o) => Number(o.id) === Number(selectedOrgId)) || organizations[0] || null
  const filteredItems = useMemo(() => {
    if (!query.trim()) return items
    const q = query.trim().toLowerCase()
    return items.filter((item) =>
      config.columns.some((c) => {
        let value = item[c]
        if (c === "customer") value = item.customer?.name || customerNameFor(item.customer_id)
        if (c.endsWith("_cents")) value = centsToAmount(value)
        return String(value ?? "")
          .toLowerCase()
          .includes(q)
      })
    )
  }, [items, query, config, customers])
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const pagedItems = filteredItems.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  if (!config) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">This section is not available.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-8">
      <InvoiceSaveConfirmDialog
        summary={pendingInvoiceSave}
        saving={invoiceSaving}
        onConfirm={executeInvoiceSave}
        onCancel={() => setPendingInvoiceSave(null)}
      />
      {canEnter && (!hideForm || editingId) ? (
      <Card id="book-primary-form">
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{editingId ? `Edit ${sectionTitle}` : `Create ${sectionTitle}`}</CardTitle>
          {editingId ? (
            <Button type="button" variant="ghost" onClick={resetForm}>
              Cancel edit
            </Button>
          ) : null}
        </CardHeader>
        <CardContent>
          {error ? <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div> : null}
          <form onSubmit={onSubmit} className={cn("grid gap-4", sectionKey === "quotes" ? "md:grid-cols-3" : "md:grid-cols-2")}>
            {isInvoices && organizationOptions.length > 0 ? (
              <div className="md:col-span-2">
                <SelectInput
                  label="From company"
                  value={selectedOrgId ? String(selectedOrgId) : ""}
                  onChange={(v) => switchInvoiceOrganization(v)}
                  options={organizationOptions}
                  error={fieldErrors.company}
                />
                {orgSwitching ? (
                  <p className="mt-1 text-xs text-muted-foreground">Loading data for {selectedOrganization?.name || "company"}…</p>
                ) : organizationOptions.length > 1 ? (
                  <p className="mt-1 text-xs text-muted-foreground">
                    Select the company this invoice is issued from. Customers and invoice numbers are scoped to that company.
                  </p>
                ) : selectedOrganization ? (
                  <p className="mt-1 text-xs text-muted-foreground">Invoices will be created for {selectedOrganization.name}.</p>
                ) : null}
              </div>
            ) : null}
            {config.fields.map((f) => {
              const value = form[f.name] ?? (f.kind === "boolean" ? false : "")
              const fieldClassName = sectionKey === "quotes" && f.wide ? "md:col-span-3" : undefined
              if (f.kind === "customer") {
                return (
                  <div key={f.name} className={fieldClassName}>
                    <SelectInput
                      label={f.label}
                      value={value}
                      onChange={(v) => setField(f.name, v)}
                      options={customerOptions}
                      error={fieldErrors[f.name]}
                    />
                  </div>
                )
              }
              if (f.kind === "currency") {
                return (
                  <div key={f.name} className={fieldClassName}>
                    <CurrencySearchSelect
                      label={f.label}
                      value={value || "USD"}
                      onChange={(v) => setField(f.name, v)}
                      error={fieldErrors[f.name]}
                    />
                  </div>
                )
              }
              if (f.kind === "select") {
                return (
                  <div key={f.name} className={fieldClassName}>
                    <SelectInput
                      label={f.label}
                      value={value}
                      onChange={(v) => setField(f.name, v)}
                      options={f.options || []}
                      error={fieldErrors[f.name]}
                    />
                  </div>
                )
              }
              if (f.kind === "boolean") {
                return (
                  <label
                    key={f.name}
                    className={cn(
                      "flex h-9 items-center gap-2 rounded-lg border border-border/90 bg-card px-2.5 text-xs shadow-sm",
                      fieldClassName,
                    )}
                  >
                    <input type="checkbox" checked={Boolean(value)} onChange={(e) => setField(f.name, e.target.checked)} />
                    <span className="text-foreground/85">{f.label}</span>
                  </label>
                )
              }
              const numberHint =
                isInvoices && f.name === "invoice_number"
                  ? invoiceNumberLoading
                    ? "Generating invoice number from database…"
                    : f.hint
                  : isStructuredQuotes && f.name === "quote_number"
                    ? quoteNumberLoading
                      ? "Generating quote number from database…"
                      : !form.customer_id
                        ? "Select a customer first — e.g. ACTPA-PEGAS-2026-Q0001"
                        : f.hint
                    : f.hint
              const quoteTotalReadOnly = isStructuredQuotes && f.name === "total_cents"
              return (
                <div key={f.name} className={fieldClassName}>
                  <TextInput
                    label={f.label}
                    type={f.kind === "date" ? "date" : f.kind === "number" ? "number" : "text"}
                    value={value}
                    placeholder={
                      isStructuredQuotes && f.name === "quote_number" && !form.customer_id
                        ? "Select customer to generate…"
                        : f.placeholder
                    }
                    onChange={(v) => setField(f.name, v)}
                    error={fieldErrors[f.name]}
                    readOnly={Boolean(f.readOnly) || quoteTotalReadOnly || (isInvoices && f.name === "invoice_number") || (isStructuredQuotes && f.name === "quote_number")}
                    hint={numberHint}
                  />
                </div>
              )
            })}
            {sectionKey === "invoices" || sectionKey === "credit-notes" ? (
              <DocumentLineRows
                value={form.lines}
                onChange={(lines) => setForm((prev) => ({ ...prev, lines }))}
                gstRateOptions={gstRateSelectOptions}
              />
            ) : null}
            {sectionKey === "quotes" ? (
              <QuoteLineRows
                value={form.lines}
                onChange={(lines) => {
                  const total = quoteLinesTotalCents(lines)
                  setForm((prev) => ({
                    ...prev,
                    lines,
                    total_cents: total > 0 ? centsToAmount(total) : prev.total_cents,
                  }))
                }}
                currency={form.currency || "USD"}
              />
            ) : null}
            <div className={cn("flex items-center gap-2", sectionKey === "quotes" ? "md:col-span-3" : "md:col-span-2")}>
              <Button type="submit" disabled={orgSwitching || Boolean(pendingInvoiceSave)}>
                {isInvoices ? (editingId ? "Review & save" : "Review & create") : editingId ? "Save" : "Create"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => reload()} disabled={orgSwitching}>
                Refresh
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
      ) : (!hideForm || editingId) && !canEnter ? (
      <Card className={setupCompany ? "border-primary/40 bg-primary/[0.06]" : undefined}>
        <CardContent className="py-4">
          {setupCompany ? (
            <>
              <p className="text-sm font-medium text-foreground">Register your company first</p>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                Actinium Book needs a company before you can create {sectionTitle.toLowerCase()}. Use Administration → Register company in the sidebar.
              </p>
              <a
                href="/admin"
                className="mt-3 inline-flex h-9 items-center justify-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition-colors hover:bg-primary/90"
              >
                Continue to company setup
              </a>
            </>
          ) : needsCompany ? (
            <p className="text-sm text-muted-foreground">
              No company is assigned to your account yet. Ask an Admin to add you to a company, or sign in with an account that has one.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Your role (<span className="font-medium capitalize">{access.access_role || "viewer"}</span>) is read-only here — you cannot create or edit{" "}
              {sectionTitle.toLowerCase()}.
            </p>
          )}
          {error && !setupCompany ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
        </CardContent>
      </Card>
      ) : null}

      <Card>
        <CardHeader className="flex-row items-center justify-between gap-3 space-y-0">
          <CardTitle>{sectionTitle} List</CardTitle>
          <div className="text-xs text-muted-foreground">{loading ? "Loading..." : `${filteredItems.length} record(s)`}</div>
        </CardHeader>
        <CardContent>
          {error && canEnter ? (
            <div className="mb-2 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700">{error}</div>
          ) : null}
          <div className="mb-2 max-w-sm">
            <TextInput label="Search" value={query} onChange={setQuery} placeholder="Filter list..." />
          </div>
          <div className="overflow-x-auto rounded-xl border border-border/90 bg-card shadow-soft">
            <table className="min-w-full border-collapse text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/35 text-left text-muted-foreground">
                  {config.columns.map((c) => (
                    <th key={c} className="px-2 py-1.5 font-medium">
                      {c === "customer" ? "Customer" : c.replaceAll("_", " ")}
                    </th>
                  ))}
                  <th className="px-2 py-1.5 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((item) => (
                  <tr key={item.id} className="border-b border-border/60 bg-card last:border-0 hover:bg-muted/20">
                    {config.columns.map((c) => {
                      let value = item[c]
                      if (c === "customer") value = item.customer?.name || customerNameFor(item.customer_id)
                      if (c === "quote_type") {
                        value = item.quote_type_label || QUOTE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value
                      }
                      if (c.endsWith("_cents")) value = centsToAmount(value)
                      if (typeof value === "boolean") value = value ? "Yes" : "No"
                      return (
                        <td key={c} className="px-2 py-1 text-foreground/85">
                          {value ?? ""}
                        </td>
                      )
                    })}
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap items-center gap-1">
                        {canEnter ? (
                          <>
                            <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs" onClick={() => hydrateForEdit(item)}>
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              className={cn("h-7 px-1.5 text-xs text-red-600 hover:text-red-700")}
                              onClick={() => onDelete(item.id)}
                            >
                              Delete
                            </Button>
                          </>
                        ) : null}
                        {sectionKey === "quotes" ? (
                          <Button
                            type="button"
                            variant="ghost"
                            className="h-7 px-1.5 text-xs text-primary"
                            onClick={() => downloadQuotePdf(item.id)}
                          >
                            PDF
                          </Button>
                        ) : null}
                        {canApprove && sectionKey === "invoices" && canApproveDocument(access, item) ? (
                          <>
                            {item.approval_status !== "approved" ? (
                              <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs text-primary" onClick={() => approveInvoice(item.id)}>
                                Approve
                              </Button>
                            ) : null}
                            {item.approval_status !== "rejected" ? (
                              <Button type="button" variant="ghost" className="h-7 px-1.5 text-xs text-amber-700" onClick={() => rejectInvoice(item.id)}>
                                Reject
                              </Button>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && filteredItems.length === 0 ? (
                  <tr>
                    <td className="px-2 py-3 text-muted-foreground" colSpan={config.columns.length + 1}>
                      No records yet. Create your first one above.
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={currentPage <= 1}>
            Previous
          </Button>
          <Button
            type="button"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage >= totalPages}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  )
}

export function SalesPage(props) {
  const {
    active_slug,
    section,
    section_title,
    user_name,
    enable_vendor_quote_reselling: enableVendorQuoteReselling = false,
    access_role,
    can_create_company,
    can_enter_transactions,
    can_approve_documents,
    can_manage_company_settings,
    is_viewer,
  } = props

  const access = accessFromProps(props)

  const activeKey = section || "customers"

  return (
    <BookShell
      activeSlug={active_slug || "sales"}
      userName={user_name}
      access={access}
      featureFlags={{ vendor_quote_reselling: Boolean(enableVendorQuoteReselling) }}
    >
      <div className="flex flex-col gap-6">
        <PageHeader
          context="Sales"
          title={section_title}
          actions={
            <>
              <a href="#" className="hidden text-[14px] font-medium text-primary hover:underline sm:inline">
                Find accountants
              </a>
              <Button
                type="button"
                className="gap-1 px-3"
                onClick={() => document.getElementById("book-primary-form")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              >
                <span className="text-base leading-none">+</span>
                New
              </Button>
            </>
          }
        />
        <SalesResourceCrud
          sectionKey={activeKey}
          sectionTitle={section_title}
          access={access}
          enableVendorQuoteReselling={Boolean(enableVendorQuoteReselling)}
        />
      </div>
    </BookShell>
  )
}

